import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Disclaimer } from "@/components/Shared";
import {
  Heart, Plane, Home as HomeIcon, Sparkles, ShieldCheck, ArrowRight,
  CheckCircle2, Users, Banknote, FileText, Clock, ScrollText,
  Camera, Phone, Video, MapPin, AlertTriangle,
} from "lucide-react";
import ConsentCheckbox from "@/components/ConsentCheckbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PATHWAYS = [
  {
    icon: HomeIcon,
    badge: "Onshore",
    title: "Subclass 820 / 801",
    subtitle: "Partner Visa — applying from inside Australia",
    desc: "For applicants inside Australia. The 820 is the temporary stage; the 801 is the permanent stage. Available to married and de facto partners of an Australian citizen, permanent resident or eligible NZ citizen.",
    bullets: ["Stage 1 — temporary 820", "Stage 2 — permanent 801", "Married or de facto"],
    accent: "from-[#29324A] to-[#1f2638]",
  },
  {
    icon: Plane,
    badge: "Offshore",
    title: "Subclass 309 / 100",
    subtitle: "Partner Visa — applying from outside Australia",
    desc: "For applicants generally outside Australia. From 25 Nov 2023, subclass 309 visas can be granted whether the applicant is inside or outside Australia. Temporary 309 progresses to permanent 100.",
    bullets: ["Stage 1 — temporary 309", "Stage 2 — permanent 100", "Spouse or de facto"],
    accent: "from-[#C43A89] to-[#C43A89]",
  },
  {
    icon: Heart,
    badge: "Engaged",
    title: "Subclass 300",
    subtitle: "Prospective Marriage Visa",
    desc: "For engaged couples planning to marry in Australia. Applicant must be outside Australia when applying. Visa holders can stay 9–15 months, work and study, and then apply for a Partner Visa.",
    bullets: ["9–15 months stay", "Work & study allowed", "Marry then transition to 820/801"],
    accent: "from-[#1B1430] to-[#29324A]",
  },
];

const EVIDENCE = [
  { icon: Banknote, title: "Financial", items: ["Joint bank account", "Shared bills", "Rent, mortgage or property documents", "Money transfers", "Shared insurance or loans"] },
  { icon: HomeIcon, title: "Household", items: ["Joint lease", "Mail to same address", "Utility bills", "Photos of living arrangements", "Shared responsibilities"] },
  { icon: Camera, title: "Social", items: ["Photos together", "Travel records", "Invitations", "Statements from family & friends", "Social media (where relevant)"] },
  { icon: Heart, title: "Commitment", items: ["Marriage certificate or registration", "Communication records", "Future plans", "Emotional support", "Long-term commitment"] },
  { icon: FileText, title: "Identity & Character", items: ["Passports", "Birth certificates", "AU & foreign police checks", "Health examination", "Sponsor documents"] },
];

const PARTNER_OPTS = [
  { v: "citizen",            l: "Australian citizen" },
  { v: "permanent_resident", l: "Australian permanent resident" },
  { v: "nz_citizen",         l: "Eligible New Zealand citizen" },
  { v: "other",              l: "Other / not sure" },
];
const REL_TYPES = [
  { v: "married",   l: "Married" },
  { v: "de_facto",  l: "De facto" },
  { v: "engaged",   l: "Engaged" },
  { v: "not_sure",  l: "Not sure" },
];
const YN = [{ v: "yes", l: "Yes" }, { v: "no", l: "No" }];
const YNS = [{ v: "yes", l: "Yes" }, { v: "no", l: "No" }, { v: "sometimes", l: "Sometimes" }];
const YNS3 = [{ v: "yes", l: "Yes" }, { v: "no", l: "No" }, { v: "not_sure", l: "Not sure" }];
const CONSULT = [
  { v: "phone",     l: "Phone", icon: Phone },
  { v: "zoom",      l: "Zoom video", icon: Video },
  { v: "in_person", l: "In person", icon: MapPin },
];
const DURATIONS = ["Less than 6 months", "6–12 months", "1–2 years", "2–5 years", "5+ years"];

export default function PartnerVisa() {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", current_location: "",
    current_visa_status: "", partner_status: "", relationship_type: "",
    duration_together: "", living_together: "", has_evidence: "",
    has_children: "", previous_refusal: "", visa_expiring_soon: "no",
    preferred_consult: "phone", notes: "",
  });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const u = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const required = ["full_name", "email", "phone", "current_location", "partner_status", "relationship_type", "duration_together", "living_together", "has_evidence", "has_children", "previous_refusal", "preferred_consult"];

  const submit = async (e) => {
    e.preventDefault();
    for (const k of required) {
      if (!form[k]) { toast.error(`Please complete: ${k.replace(/_/g, " ")}`); return; }
    }
    if (!/\S+@\S+\.\S+/.test(form.email)) { toast.error("Please enter a valid email."); return; }
    if (!consent) { toast.error("Please tick the consent checkbox."); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/partner-visa/submit`, {
        ...form, consent: true, consent_timestamp: new Date().toISOString(),
      });
      setDone(r.data);
      toast.success("Assessment received — our team will be in touch.");
      window.scrollTo({ top: document.getElementById("assessment-form").offsetTop - 80, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="partner-visa-page" className="bg-white">

      {/* ───── HERO ───── */}
      <section className="relative bg-gradient-to-br from-[#29324A] via-[#1B1430] to-[#29324A] text-white overflow-hidden grain">
        <span aria-hidden className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#29324A] via-[#C43A89] to-[#C43A89]" />
        <span aria-hidden className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#C43A89]/25 blur-3xl" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 relative">
          <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">
            <Heart className="h-3 w-3" /> Partner Visa Australia 2026
          </span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-[3.4rem] leading-[1.05] tracking-tight mt-4 max-w-3xl">
            Bring your partner to Australia with the <span className="text-[#C43A89]">right visa pathway</span>.
          </h1>
          <p className="text-[15px] md:text-base text-slate-200 leading-relaxed mt-5 max-w-2xl">
            Partner Visas allow the spouse, de facto partner, fiancé or prospective spouse of an Australian citizen, permanent resident or eligible New Zealand citizen to live in Australia — and progress to permanent residency. Department of Home Affairs confirms options across subclasses 300, 309 / 100 and 820 / 801.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#assessment-form" className="inline-flex items-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] text-white text-xs font-bold tracking-wider uppercase px-6 py-3.5 btn-shimmer transition-colors">
              Start Free Partner Visa Assessment <ArrowRight className="h-3.5 w-3.5" />
            </a>
            <Link to="/book-consultation" className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/20 text-white text-xs font-bold tracking-wider uppercase px-6 py-3.5 transition-colors">
              Book a Consultation <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ───── WHICH VISA IS RIGHT FOR YOU ───── */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">Which Partner Visa is right for you?</span>
            <h2 className="font-display text-3xl md:text-4xl text-[#29324A] tracking-tight leading-tight mt-3">
              Three pathways — your situation decides which applies.
            </h2>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-4 md:gap-5">
            {PATHWAYS.map((p, i) => (
              <article key={i} className="bg-white border border-slate-200 hover:border-[#C43A89] hover:shadow-[0_22px_50px_-20px_rgba(196,58,137,0.25)] transition-all p-7 relative overflow-hidden">
                <span aria-hidden className={`absolute top-0 left-0 h-1 w-20 bg-gradient-to-r ${p.accent}`} />
                <div className="flex items-center justify-between">
                  <p.icon className="h-8 w-8 text-[#29324A]" />
                  <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#C43A89] bg-[#C43A89]/10 px-2 py-1">{p.badge}</span>
                </div>
                <h3 className="font-display text-xl text-[#29324A] mt-4 leading-snug">{p.title}</h3>
                <div className="text-[11.5px] tracking-wider uppercase text-slate-500 font-semibold mt-1">{p.subtitle}</div>
                <p className="text-[13.5px] text-slate-700 leading-relaxed mt-3">{p.desc}</p>
                <ul className="mt-4 space-y-1.5">
                  {p.bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-2 text-[12.5px] text-slate-700"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" /> {b}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>

          {/* 2026 Update banner */}
          <div className="mt-8 bg-[#F8F4FB] border-l-4 border-[#29324A] p-5 flex items-start gap-3">
            <Sparkles className="h-5 w-5 text-[#29324A] mt-0.5 shrink-0" />
            <p className="text-[13.5px] text-slate-700 leading-relaxed">
              <strong className="text-[#29324A]">2026 update:</strong> Home Affairs confirms that from 25 November 2023, subclass 309 visas can be granted whether the applicant is inside or outside Australia.
            </p>
          </div>
        </div>
      </section>

      {/* ───── GOVERNMENT FEES ───── */}
      <section className="py-16 md:py-20 bg-[#F8F9FA]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-12 gap-8 items-start">
            <div className="md:col-span-5">
              <span className="text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">Government Fees 2026</span>
              <h2 className="font-display text-3xl md:text-4xl text-[#29324A] tracking-tight leading-tight mt-3">
                Partner Visa Government Fees.
              </h2>
              <p className="text-[14px] text-slate-600 leading-relaxed mt-4">
                Current Home Affairs charge for most Partner Visa applicants. Always confirm the latest amount using the official Home Affairs Visa Pricing Estimator before lodging.
              </p>
            </div>
            <div className="md:col-span-7 grid sm:grid-cols-2 gap-4">
              <div className="bg-white border border-slate-200 p-6 relative overflow-hidden">
                <span aria-hidden className="absolute top-0 left-0 h-1 w-16 bg-gradient-to-r from-[#29324A] to-[#C43A89]" />
                <div className="text-[10px] tracking-wider uppercase text-slate-500 font-bold">Most Partner Visa applicants</div>
                <div className="font-display text-3xl text-[#29324A] mt-2">from AUD <span className="text-[#C43A89]">$11,710</span></div>
                <p className="text-[12px] text-slate-500 mt-2"><span className="line-through text-slate-400">$9,365</span> <span className="ml-1 text-[#C43A89] font-bold">New · 1 Jul 2026</span></p>
              </div>
              <div className="bg-white border border-slate-200 p-6 relative overflow-hidden">
                <span aria-hidden className="absolute top-0 left-0 h-1 w-16 bg-gradient-to-r from-[#C43A89] to-[#C43A89]" />
                <div className="text-[10px] tracking-wider uppercase text-slate-500 font-bold">Subclass 820 after holding 300</div>
                <div className="font-display text-3xl text-[#29324A] mt-2">from AUD <span className="text-[#C43A89]">$1,955</span></div>
                <p className="text-[12px] text-slate-500 mt-2">Reduced fee for applicants transitioning from subclass 300.</p>
              </div>
            </div>
          </div>
          <div className="mt-6 text-[12.5px] text-slate-500 italic">
            Note: Government fees can change, usually around 1 July. Always confirm the latest amount using the official Home Affairs Visa Pricing Estimator before lodging.
          </div>
        </div>
      </section>

      {/* ───── BASIC ELIGIBILITY ───── */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">Basic Eligibility</span>
            <h2 className="font-display text-3xl md:text-4xl text-[#29324A] tracking-tight leading-tight mt-3">You may be eligible if…</h2>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 gap-3">
            {[
              "You are married to, in a de facto relationship with, or engaged to an Australian citizen, permanent resident or eligible NZ citizen.",
              "Your relationship is genuine and continuing.",
              "You and your partner have a mutual commitment to a shared life.",
              "You meet health and character requirements.",
              "Your sponsor meets sponsorship and character requirements.",
              "For de facto relationships, the relationship generally needs to exist for at least 12 months (unless an exemption applies, such as relationship registration or compelling circumstances).",
            ].map((e, i) => (
              <div key={i} className="flex items-start gap-3 bg-[#F8F4FB] border-l-4 border-[#29324A] p-4">
                <CheckCircle2 className="h-4.5 w-4.5 text-emerald-600 mt-0.5 shrink-0" />
                <span className="text-[13.5px] text-slate-700 leading-relaxed">{e}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── EVIDENCE CHECKLIST ───── */}
      <section className="py-16 md:py-20 bg-[#FDF2F8]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">Relationship evidence checklist</span>
            <h2 className="font-display text-3xl md:text-4xl text-[#29324A] tracking-tight leading-tight mt-3">A strong application is built around evidence.</h2>
            <p className="text-[13.5px] text-slate-600 leading-relaxed mt-3">Home Affairs says sponsors should provide Australian and foreign police checks when submitting the sponsorship form.</p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {EVIDENCE.map((c, i) => (
              <div key={i} className="bg-white border border-slate-200 p-6 hover:border-[#C43A89] transition-colors">
                <c.icon className="h-7 w-7 text-[#C43A89]" />
                <h3 className="font-display text-lg text-[#29324A] mt-3 leading-snug">{c.title}</h3>
                <ul className="mt-3 space-y-1.5">
                  {c.items.map((it, j) => (
                    <li key={j} className="flex items-start gap-2 text-[12.5px] text-slate-700"><span className="text-[#C43A89] mt-0.5">•</span> {it}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── TWO-STAGE PROCESS ───── */}
      <section className="py-16 md:py-20 bg-[#29324A] text-white relative overflow-hidden grain">
        <span aria-hidden className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#29324A] via-[#C43A89] to-[#C43A89]" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">Two-Stage Process</span>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-tight mt-3">Partner visas usually involve two stages.</h2>
          </div>
          <div className="mt-10 grid md:grid-cols-2 gap-4 md:gap-6">
            <div className="bg-white/5 border border-white/10 p-7">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#C43A89] font-bold">Stage 1</div>
              <h3 className="font-display text-2xl mt-2">Temporary visa</h3>
              <p className="text-[14px] text-slate-200 leading-relaxed mt-3">Subclass <strong className="text-white">820</strong> (onshore) or subclass <strong className="text-white">309</strong> (offshore). You and your sponsor lodge documents proving a genuine relationship.</p>
            </div>
            <div className="bg-white/5 border border-white/10 p-7">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#C43A89] font-bold">Stage 2</div>
              <h3 className="font-display text-2xl mt-2">Permanent visa</h3>
              <p className="text-[14px] text-slate-200 leading-relaxed mt-3">Subclass <strong className="text-white">801</strong> or subclass <strong className="text-white">100</strong>. Home Affairs states two years must have passed since you first applied before you are eligible for the permanent stage.</p>
            </div>
          </div>
          <p className="text-[12.5px] text-slate-300 mt-6 max-w-3xl">
            Applicants complete Stage 2 through ImmiAccount by selecting: <em>New application → Family → Stage 2 Permanent Partner Visa Assessment 100, 801.</em>
          </p>
        </div>
      </section>

      {/* ───── STATUS + PROCESSING ───── */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-5 md:gap-7">
          <div className="bg-white border border-slate-200 p-7 relative overflow-hidden">
            <span aria-hidden className="absolute top-0 left-0 h-1 w-16 bg-gradient-to-r from-[#29324A] to-[#C43A89]" />
            <ScrollText className="h-7 w-7 text-[#29324A]" />
            <h3 className="font-display text-xl text-[#29324A] mt-3">How to check your visa status</h3>
            <p className="text-[13.5px] text-slate-700 leading-relaxed mt-3">Applicants who applied online can check their application through <strong>ImmiAccount</strong>. Visa holders can check visa details and conditions through <strong>VEVO</strong>.</p>
          </div>
          <div className="bg-white border border-slate-200 p-7 relative overflow-hidden">
            <span aria-hidden className="absolute top-0 left-0 h-1 w-16 bg-gradient-to-r from-[#C43A89] to-[#C43A89]" />
            <Clock className="h-7 w-7 text-[#C43A89]" />
            <h3 className="font-display text-xl text-[#29324A] mt-3">Processing times</h3>
            <p className="text-[13.5px] text-slate-700 leading-relaxed mt-3">Use the official <em>Global Visa Processing Times Guide</em> — updated monthly. Processing depends on application strength, document quality, health and character checks, relationship evidence and Department workload. A complete and well-prepared application may help reduce avoidable delays.</p>
          </div>
        </div>
      </section>

      {/* ───── IF THE RELATIONSHIP ENDS + FAMILY VIOLENCE ───── */}
      <section className="py-16 md:py-20 bg-[#F8F4FB]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-5 md:gap-7">
          <div className="bg-white border border-slate-200 p-7">
            <Users className="h-7 w-7 text-[#29324A]" />
            <h3 className="font-display text-xl text-[#29324A] mt-3">If the relationship ends</h3>
            <p className="text-[13.5px] text-slate-700 leading-relaxed mt-3">A partner visa application may still continue in limited circumstances, including where:</p>
            <ul className="mt-3 space-y-1.5">
              {["The sponsor has died.", "The applicant or family member has experienced domestic or family violence.", "The couple has a child and shares parental responsibility."].map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-[13px] text-slate-700"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" /> {s}</li>
              ))}
            </ul>
            <p className="text-[12px] text-slate-500 mt-3 italic">Home Affairs confirms these relationship cessation pathways.</p>
          </div>
          <div className="bg-rose-50 border border-rose-200 p-7">
            <ShieldCheck className="h-7 w-7 text-rose-600" />
            <h3 className="font-display text-xl text-rose-900 mt-3">Family violence protection</h3>
            <p className="text-[13.5px] text-rose-900 leading-relaxed mt-3">
              Applicants do not have to stay in an unsafe relationship to remain eligible for a visa. Home Affairs states that people experiencing domestic and family violence may still be eligible for the grant of a permanent visa.
            </p>
            <p className="text-[12.5px] text-rose-800 mt-3 italic">If you are experiencing family violence, seek urgent safety support first. Migration advice should be handled carefully and confidentially.</p>
          </div>
        </div>
      </section>

      {/* ───── ASSESSMENT FORM ───── */}
      <section id="assessment-form" className="py-16 md:py-24 bg-gradient-to-br from-[#29324A] via-[#1B1430] to-[#29324A] text-white relative overflow-hidden grain">
        <span aria-hidden className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#C43A89]/25 blur-3xl" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-2xl mx-auto">
            <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase"><Sparkles className="h-3 w-3" /> Free Partner Visa Assessment</span>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-tight mt-3">Check your Partner Visa pathway.</h2>
            <p className="text-[14px] text-slate-200 leading-relaxed mt-4">Answer a few quick questions so our migration team can assess whether you may be eligible for an onshore partner visa, offshore partner visa or prospective marriage visa.</p>
          </div>

          {done ? (
            <div data-testid="partner-result" className="mt-10 bg-white text-slate-900 p-8 md:p-10">
              <span aria-hidden className="block h-1 w-24 bg-gradient-to-r from-[#29324A] to-[#C43A89] mb-5" />
              <div className="text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">Thank you, {form.full_name.split(" ")[0]}</div>
              <h3 className="font-display text-3xl text-[#29324A] mt-2 leading-tight">Initial pathway suggestion</h3>
              <div className="mt-5 bg-[#F8F4FB] border-l-4 border-[#29324A] p-5">
                <div className="text-[11px] tracking-wider uppercase text-[#29324A] font-bold mb-1">Recommended starting point</div>
                <div className="font-display text-xl text-[#29324A]">{done.recommended_pathway}</div>
              </div>
              <p className="text-[13.5px] text-slate-700 leading-relaxed mt-5">
                A member of our migration team will review your responses and contact you within 1 business day. Please prepare your relationship evidence in the meantime — financial, household, social and commitment documents listed above.
              </p>
              <div className="flex flex-wrap gap-3 mt-6">
                <Link to="/book-consultation" className="bg-[#C43A89] hover:bg-[#a22b6e] text-white text-xs font-bold tracking-wider uppercase px-5 py-3 inline-flex items-center gap-1.5">Book consultation <ArrowRight className="h-3.5 w-3.5" /></Link>
                <button onClick={() => { setDone(null); setForm({ ...form, full_name: "", email: "", phone: "" }); }} className="bg-white border border-slate-300 hover:border-[#29324A] text-slate-700 text-xs font-bold tracking-wider uppercase px-5 py-3">Submit another</button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-10 bg-white text-slate-900 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.5)]">
              <div className="p-6 md:p-8 space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <F label="Full name *"><Input data-testid="pv-name" required value={form.full_name} onChange={(e) => u("full_name", e.target.value)} /></F>
                  <F label="Email *"><Input data-testid="pv-email" type="email" required value={form.email} onChange={(e) => u("email", e.target.value)} /></F>
                  <F label="Phone *"><Input data-testid="pv-phone" required value={form.phone} onChange={(e) => u("phone", e.target.value)} /></F>
                  <F label="Currently in… *">
                    <Select value={form.current_location} onValueChange={(v) => u("current_location", v)}>
                      <SelectTrigger data-testid="pv-location"><SelectValue placeholder="Select location" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="in_australia">In Australia</SelectItem>
                        <SelectItem value="outside_australia">Outside Australia</SelectItem>
                      </SelectContent>
                    </Select>
                  </F>
                  <F label="Current visa status (if any)">
                    <Input data-testid="pv-current-visa" value={form.current_visa_status} onChange={(e) => u("current_visa_status", e.target.value)} placeholder="e.g. Student 500 · Visitor 600 · TR 485" />
                  </F>
                  <F label="Partner is *">
                    <Select value={form.partner_status} onValueChange={(v) => u("partner_status", v)}>
                      <SelectTrigger data-testid="pv-partner-status"><SelectValue placeholder="Select partner status" /></SelectTrigger>
                      <SelectContent>{PARTNER_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                  <F label="Relationship type *">
                    <Select value={form.relationship_type} onValueChange={(v) => u("relationship_type", v)}>
                      <SelectTrigger data-testid="pv-rel-type"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{REL_TYPES.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                  <F label="How long have you been together? *">
                    <Select value={form.duration_together} onValueChange={(v) => u("duration_together", v)}>
                      <SelectTrigger data-testid="pv-duration"><SelectValue placeholder="Select duration" /></SelectTrigger>
                      <SelectContent>{DURATIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                  <F label="Do you live together? *">
                    <Select value={form.living_together} onValueChange={(v) => u("living_together", v)}>
                      <SelectTrigger data-testid="pv-living"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{YNS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                  <F label="Do you have relationship evidence? *">
                    <Select value={form.has_evidence} onValueChange={(v) => u("has_evidence", v)}>
                      <SelectTrigger data-testid="pv-evidence"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{YNS3.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                  <F label="Do you have children together? *">
                    <Select value={form.has_children} onValueChange={(v) => u("has_children", v)}>
                      <SelectTrigger data-testid="pv-children"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{YN.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                  <F label="Previous visa refusal or cancellation? *">
                    <Select value={form.previous_refusal} onValueChange={(v) => u("previous_refusal", v)}>
                      <SelectTrigger data-testid="pv-refusal"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{YN.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                  <F label="Is your current visa expiring soon?">
                    <Select value={form.visa_expiring_soon} onValueChange={(v) => u("visa_expiring_soon", v)}>
                      <SelectTrigger data-testid="pv-expiring"><SelectValue /></SelectTrigger>
                      <SelectContent>{YN.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                  <F label="Preferred consultation *">
                    <Select value={form.preferred_consult} onValueChange={(v) => u("preferred_consult", v)}>
                      <SelectTrigger data-testid="pv-consult"><SelectValue /></SelectTrigger>
                      <SelectContent>{CONSULT.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                </div>

                <F label="Anything else you'd like our team to know (optional)">
                  <Textarea data-testid="pv-notes" rows={3} value={form.notes} onChange={(e) => u("notes", e.target.value)} placeholder="Share any additional context — refusal history, special circumstances, urgent timelines, etc." />
                </F>

                <ConsentCheckbox
                  value={consent}
                  onChange={setConsent}
                  testid="pv-consent"
                  label="I understand this form provides general information only and does not guarantee visa eligibility or visa grant. I consent to Shamima Yasmin Anwar contacting me about my enquiry."
                />

                <Button data-testid="pv-submit" type="submit" disabled={submitting || !consent} className="w-full bg-gradient-to-br from-[#29324A] to-[#C43A89] hover:from-[#1f2638] hover:to-[#a22b6e] text-white rounded-none py-5 font-semibold text-[15px] btn-shimmer disabled:opacity-50">
                  {submitting ? "Submitting…" : "Check My Partner Visa Options"}
                </Button>

                <p className="text-[11px] text-slate-500 leading-relaxed flex items-start gap-2">
                  <AlertTriangle className="h-3.5 w-3.5 text-[#C43A89] mt-0.5 shrink-0" />
                  This page provides general information only and does not constitute legal or migration advice. Visa criteria, fees and processing times may change. Always check the latest information from the Department of Home Affairs or speak with a Registered Migration Agent before applying.
                </p>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* ───── CTA ───── */}
      <section className="py-14 bg-[#FDF2F8]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="font-display text-2xl md:text-3xl text-[#29324A] leading-tight">Not sure which Partner Visa applies to you?</h3>
          <p className="text-[14px] text-slate-700 leading-relaxed mt-3 max-w-2xl mx-auto">
            Partner visa applications are highly evidence-based. A small mistake, missing document or weak relationship explanation can delay the process or create unnecessary risk. Book a consultation with a Registered Migration Agent to understand your pathway before lodging.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link to="/book-consultation" className="inline-flex items-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] text-white text-xs font-bold tracking-wider uppercase px-6 py-3.5 btn-shimmer transition-colors">
              Book a Partner Visa Consultation <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <a href="#assessment-form" className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:border-[#29324A] text-slate-700 text-xs font-bold tracking-wider uppercase px-6 py-3.5 transition-colors">
              Start Free Assessment <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </section>

      <Disclaimer />
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-bold tracking-wider text-slate-600 uppercase mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
